import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Trophy, Ticket, Users, BarChart3, Coins } from "lucide-react";
import { Logo } from "../components/Logo";
import { images as imgs } from "../assets/images";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

function HeroLine({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const features = [
  {
    icon: Ticket,
    title: "Kuponger åt spelarna",
    text: "Admin lägger alla spel och väljer vilken spelare kupongen tillhör. Insats i tokens, totalodds och möjlig vinst räknas ut direkt.",
  },
  {
    icon: Users,
    title: "Din egen spelarlista",
    text: "Lägg till kompisar, kollegor eller laget. Varje namn får sin egen resultaträkning — helt utan egna konton.",
  },
  {
    icon: BarChart3,
    title: "Resultat i realtid",
    text: "Totalöversikt plus per-spelarstatistik: satsade tokens, vinster och nettoresultat samlat i en tydlig tabell.",
  },
  {
    icon: Trophy,
    title: "Aktiva & avgjorda",
    text: "Följ pågående kuponger i en meny och historiken i en annan. Matcher avgörs automatiskt när tiden passerat.",
  },
];

const marqueeItems = [
  "FANTASY BET",
  "TOKEN-BETTING FÖR KOMPISGÄNGET",
  "1 · X · 2",
  "AKTIVA KUPONGER",
  "RESULTAT I REALTID",
  "INGA RIKTIGA PENGAR",
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <div className="grain bg-[#FAFAFC] text-slate-900 min-h-screen">
      {/* Nav */}
      <header className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto flex items-center justify-between py-6" data-testid="landing-nav">
        <div className="flex items-center gap-3">
          <Logo />
          <span className="font-display font-bold text-lg tracking-tight">Fantasy Bet</span>
        </div>
        <Link
          to="/login"
          data-testid="nav-admin-login-link"
          className="group inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors"
        >
          Admin-login
          <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pt-10 lg:pt-16 pb-20 lg:pb-28" data-testid="landing-hero">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-600 mb-6"
            >
              Låtsasbetting · Tokens · Noll risk
            </motion.p>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-7xl" data-testid="hero-heading">
              <HeroLine delay={0.3}>Spela på låtsas.</HeroLine>
              <HeroLine delay={0.45}>
                Vinn på <span className="text-blue-600">riktigt.</span>
              </HeroLine>
            </h1>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-7 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed"
            >
              Fantasy Bet är token-betting för kompisgänget. Spela på riktiga matcher med riktiga odds
              från bookmakers, lägg kuponger åt varje spelare och följ hela ligans resultat — utan en enda riktig krona.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/login"
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-2.5 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-semibold px-7 py-3.5 rounded-full transition-colors shadow-lg shadow-blue-600/20"
              >
                Kom igång
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Coins size={16} className="text-blue-600" />
                Bara tokens. Aldrig pengar.
              </div>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: imgY }} className="lg:col-span-5" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}>
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img src={imgs.hero_stadium} alt="Stadion i ljus" className="w-full h-[420px] object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-slate-200 bg-[#F8FAFC] py-4 overflow-hidden" data-testid="landing-marquee">
        <div className="marquee-track gap-12">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="flex items-center gap-12 font-display font-semibold text-sm tracking-[0.2em] text-slate-400 whitespace-nowrap">
              {item}
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 inline-block" />
            </span>
          ))}
        </div>
      </div>

      {/* Features bento */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-20 lg:py-28" data-testid="landing-features">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-600 mb-4">Så funkar det</p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
            En admin. En spelarlista. Hela säsongen i ett verktyg.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-14">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i}
              className={`group bg-white border border-slate-200 rounded-3xl p-8 hover:shadow-xl hover:shadow-slate-900/5 hover:border-slate-300 transition-shadow ${i % 2 === 0 ? "md:col-span-7" : "md:col-span-5"}`}
              data-testid={`feature-card-${i}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-6 transition-transform group-hover:-translate-y-0.5">
                <f.icon size={20} />
              </div>
              <h3 className="font-display font-semibold text-xl mb-3">{f.title}</h3>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-md">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Visual strip */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-20 lg:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[imgs.match_stadium, imgs.soccer_goal, imgs.trophy_ball].map((src, i) => (
            <motion.div
              key={src}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`rounded-3xl overflow-hidden border border-slate-200 ${i === 1 ? "md:translate-y-10" : ""}`}
            >
              <img src={src} alt="Sportmoment" className="w-full h-64 object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative bg-slate-900 rounded-[2.5rem] px-8 sm:px-14 py-16 sm:py-20 overflow-hidden"
          data-testid="landing-cta"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-blue-600/20 blur-3xl" />
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-400 mb-5">Redo att sätta igång?</p>
          <h2 className="font-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl max-w-2xl leading-tight">
            Skapa din första match och lägg första kupongen idag.
          </h2>
          <Link
            to="/login"
            data-testid="cta-login-button"
            className="group mt-10 inline-flex items-center gap-2.5 bg-white hover:bg-slate-100 active:scale-[0.98] text-slate-900 font-semibold px-7 py-3.5 rounded-full transition-colors"
          >
            Logga in som admin
            <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </section>

      <footer className="border-t border-slate-200 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <Logo size={24} />
          <span className="font-display font-semibold text-sm">Fantasy Bet</span>
        </div>
        <p className="text-xs text-slate-500">Låtsasbetting med tokens. Inga riktiga pengar involveras.</p>
      </footer>
    </div>
  );
}
